function embassyApiBase() {
  const base = process.env.REACT_APP_PRODUCTS_API_BASE_URL;
  if (!base || typeof base !== "string") {
    throw new Error("API configuration error.");
  }
  return base.replace(/\/$/, "");
}

/**
 * Search embassy Cities (prefix match).
 * @param {{ countryCode: string, cityPrefix: string, accessToken: string, signal?: AbortSignal }} params
 * @returns {Promise<Array<{ countryCode?: string, cityName?: string, postalCode?: string }>>}
 */
export async function fetchEmbassyCities({
  countryCode,
  cityPrefix,
  accessToken,
  signal,
}) {
  if (!accessToken) {
    throw new Error("Your session has expired. Please start again.");
  }
  const cc = String(countryCode || "").trim().toUpperCase();
  const city = String(cityPrefix || "").trim();
  if (!cc || city.length < 3) return [];

  const params = new URLSearchParams({ CountryCode: cc, City: city });
  const url = `${embassyApiBase()}/services/embassy/api/Cities?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "text/plain, application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  });

  const rawBody = await response.text();
  if (!response.ok) {
    let parsed = null;
    try {
      parsed = rawBody ? JSON.parse(rawBody) : null;
    } catch (_) {
      // ignore
    }
    const msg =
      (parsed && typeof parsed.message === "string" && parsed.message.trim()) ||
      (typeof rawBody === "string" && rawBody.trim()) ||
      `Request failed (${response.status})`;
    throw new Error(msg);
  }

  if (!rawBody || !rawBody.trim()) return [];
  try {
    const parsed = JSON.parse(rawBody);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

/**
 * POST JSON to embassy API with bearer auth; parses JSON body on success.
 */
async function embassyPostJson(path, accessToken, body) {
  if (!accessToken) {
    throw new Error("Your session has expired. Please start again.");
  }
  const url = `${embassyApiBase()}${path}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });

  const rawBody = await response.text();
  let parsed = null;
  try {
    parsed = rawBody ? JSON.parse(rawBody) : null;
  } catch (_) {
    // non-JSON error bodies fall through below
  }

  if (!response.ok) {
    const msg =
      (parsed && typeof parsed.message === "string" && parsed.message.trim()) ||
      (typeof rawBody === "string" && rawBody.trim()) ||
      `Request failed (${response.status})`;
    throw new Error(msg);
  }

  return parsed;
}

export async function lockSlot({ accessToken, slotId, passportNumber }) {
  const data = await embassyPostJson(
    "/services/embassy/api/slots/lock",
    accessToken,
    { slotId, passportNumber }
  );
  if (
    !data ||
    typeof data !== "object" ||
    (typeof data.lockId !== "number" && typeof data.lockId !== "string")
  ) {
    throw new Error("Invalid response from slot lock.");
  }
  const lockId = typeof data.lockId === "string" ? Number(data.lockId) : data.lockId;
  if (!Number.isFinite(lockId)) {
    throw new Error("Invalid response from slot lock.");
  }
  return { lockId, expiresAt: data.expiresAt };
}

export async function createAppointment({ accessToken, body }) {
  const data = await embassyPostJson(
    "/services/embassy/api/appointments/create",
    accessToken,
    body
  );
  const ref =
    data && typeof data === "object" && typeof data.appointmentReference === "string"
      ? data.appointmentReference.trim()
      : "";
  if (!ref) {
    throw new Error("Invalid response from appointment creation.");
  }
  return { appointmentReference: ref };
}

export async function createInternationalOrder({ accessToken, body }) {
  const data = await embassyPostJson(
    "/services/embassy/api/Orders/CreateInternational",
    accessToken,
    body
  );
  const paymentUrl =
    data && typeof data === "object" && typeof data.paymentUrl === "string"
      ? data.paymentUrl.trim()
      : "";
  const referenceNumber =
    data && typeof data === "object" && typeof data.referenceNumber === "string"
      ? data.referenceNumber.trim()
      : "";
  if (!paymentUrl) {
    throw new Error("No payment URL returned from order creation.");
  }
  return { paymentUrl, referenceNumber };
}

function orNull(val) {
  return val !== undefined && val !== "" ? val : null;
}

function pointFromCoords(coords) {
  if (!coords || typeof coords !== "object") {
    return { latitude: null, longitude: null };
  }
  const lat = Number(coords.lat);
  const lng = Number(coords.lng);
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return { latitude: null, longitude: null };
  }
  return { latitude: String(lat), longitude: String(lng) };
}

/**
 * @param {object} values
 * @param {object} receiverFormData
 * @param {{ accessToken?: string, locale?: string }} [options]
 */
export async function buildCreateInternationalPayload(values, receiverFormData, options = {}) {
  const { locale: optionsLocale } = options;
  const sender = (values.shippingDetails && values.shippingDetails.sender) || {};
  const apiBase = (process.env.REACT_APP_PRODUCTS_API_BASE_URL || "").replace(/\/$/, "");
  const locale = optionsLocale || values.locale || "en";

  let receiverCountryCode = null;
  const formCountryCode = receiverFormData.countryCode;
  if (formCountryCode != null && String(formCountryCode).trim() !== "") {
    receiverCountryCode = String(formCountryCode).trim();
  }
  if (!receiverCountryCode && orNull(receiverFormData.country) === "United Arab Emirates") {
    receiverCountryCode = "AE";
  }
  if (
    !receiverCountryCode &&
    (orNull(receiverFormData.country) === "Egypt" ||
      orNull(receiverFormData.country) === "مصر")
  ) {
    receiverCountryCode = "EG";
  }

  const receiverMobile = receiverFormData.mobile
    ? `+${receiverFormData.mobileDialCode || ""} ${receiverFormData.mobile}`.trim()
    : null;

  const receiverName = [receiverFormData.firstName, receiverFormData.lastName]
    .filter(Boolean)
    .join(" ") || null;

  const receiverPoint = pointFromCoords(receiverFormData.coords);

  const billingCityName = orNull(sender.cityName) || orNull(sender.city);

  return {
    embassyCode: orNull(values.embassyCode),
    serviceCode: orNull(values.serviceTypeCode),
    serviceTypeName: orNull(values.serviceTypeName),
    senderAddress: {
      city: orNull(sender.cityCode),
      area: orNull(sender.area),
      streetName: orNull(sender.street),
      building: orNull(sender.building),
      floor: orNull(sender.floor),
      apartmentNo: orNull(sender.apartmentNo),
      fullAddress: orNull(sender.additionalAddress),
    },
    senderContactDetails: {
      firstName: orNull(sender.firstName),
      secondName: null,
      thirdName: null,
      lastName: orNull(sender.lastName),
      passportNumber: orNull(values.passportNumber),
      mobileNumber: orNull(sender.mobile),
      emailAddress: orNull(sender.email),
      receiptNumber: null,
      nationalIdNumber: null,
      applicationReferenceNumber: null,
      notes: null,
    },
    receiverDetails: {
      contact: {
        name: receiverName,
        mobileNumber: receiverMobile,
        phoneNumber: null,
        emailAddress: orNull(receiverFormData.email),
        companyName: orNull(receiverFormData.companyName),
        altMobileNumber: null,
      },
      address: {
        line1: orNull(receiverFormData.additionalAddress),
        line2: null,
        regionCode: orNull(receiverFormData.area),
        city: orNull(receiverFormData.cityName) || orNull(receiverFormData.cityCode),
        cityCode: orNull(receiverFormData.cityCode),
        state: orNull(receiverFormData.state),
        countryCode: receiverCountryCode,
        zipCode: orNull(receiverFormData.zip),
        point: receiverPoint,
        category: "Residential",
        streetName: orNull(receiverFormData.street),
        landmark: orNull(receiverFormData.area),
        buildingName: orNull(receiverFormData.building),
        floorNo: orNull(receiverFormData.floor),
        apartmentNo: orNull(receiverFormData.apartmentNo),
        officeId: null,
        countryName: (() => {
          const fromForm = orNull(receiverFormData.country);
          if (fromForm) return fromForm;
          if (receiverCountryCode === "EG") {
            return locale === "ar" ? "مصر" : "Egypt";
          }
          return "United Arab Emirates";
        })(),
      },
      referenceNo1: null,
      referenceNo2: null,
    },
    attestationData: {
      attestationNatureCode: null,
      attestationNatureName: null,
      attestationTypeCode: null,
      attestationTypeName: null,
      isAlreadyAttested: false,
    },
    preferredDateCollect: null,
    preferredTimeCollect: null,
    pieces: null,
    specialNotes: null,
    paymentRequest: {
      billingAddress: {
        firstName: orNull(sender.firstName),
        lastName: orNull(sender.lastName),
        address: orNull(sender.additionalAddress),
        city: billingCityName,
        countryName: "United Arab Emirates",
      },
      redirectUrl: `${apiBase}/embassy/payment-summary?locale=${locale}&order`,
    },
    documentAsBase64: null,
    orderExtraData: null,
    referenceNumber: orNull(values.goodConductAppointmentReference),
    accountNumber: null,
  };
}

export function buildGoodConductAppointmentPayload(values, lockId) {
  const service =
    typeof values.serviceTypeCode === "string" && values.serviceTypeCode.trim()
      ? values.serviceTypeCode.trim()
      : "GOOD";
  const mobileNumber = `${values.mobileDialCode || ""}${values.mobileNumber || ""}`;
  return {
    lockId,
    firstName: values.firstName,
    lastName: values.lastName,
    passportNumber: values.passportNumber,
    mobileNumber,
    email: values.emailAddress,
    service,
    isShipmentRequired: Boolean(values.goodConductShippingSelected),
  };
}
